import { formatCurrency } from '../../shared/format_currency.js';
import type { CheckoutCopy, CheckoutMotiveCopy } from '../model/checkout_copy.js';
import type { CheckoutMoney, CheckoutSubmitBlocker } from '../model/checkout_source.js';
import { CheckoutPriceSummary } from '../summary/checkout_price_summary.js';
import { useCheckoutEnv } from './env_context.js';
import type { CheckoutSubmitNotice } from './checkout_subject.js';

export interface CheckoutPricePanelProps {
  /** Number of daily rates (optional — omits the row when absent). */
  days?: number;
  /** One breakdown row per checkout item (multi-trecho flight: a line per leg),
   *  rendered before the total. Absent ⇒ no per-item breakdown (single item). */
  items?: { id: string; label: string; price: CheckoutMoney }[];
  perTraveler?: CheckoutMoney;
  total: CheckoutMoney;
  /** Substantivo do produto no CTA ("Solicitar voo · R$ 1.182"). */
  noun: string;
  /** Faixa âmbar entre o CTA e a nota (Figma 614-7416). O retry é o próprio
   *  submit — re-dispara a mesma ação montada do estado corrente do form. */
  submitNotice?: CheckoutSubmitNotice;
  onSubmit: () => void;
  /** Saída secundária do rodapé — o MESMO fechamento do ✕ do topbar (a
   *  confirmação de descarte é do host, não daqui). Existe porque no touch o
   *  ✕ mora longe do polegar (e, no iPhone, sob a status bar até o fix de
   *  safe-area); o CSS a esconde no desktop, onde ✕ e Escape já servem. */
  onBack?: () => void;
  submitting: boolean;
  canSubmit: boolean;
  /** O que ainda trava o CTA — nomeado junto a ele e ligado por
   *  `aria-describedby`; sem isto o botão ficava cinza sem motivo (#4206). */
  submitBlockers?: CheckoutSubmitBlocker[];
  /** Como o produto pergunta o motivo; ausente, o da viagem. */
  motive?: CheckoutMotiveCopy;
}

/** "Para solicitar, preencha: telefone do viajante e motivo da viagem". Some
 *  quando nada falta; enquanto envia, quem a esconde é o `CheckoutPriceSummary`. */
export function blockersMessage(
  copy: CheckoutCopy,
  locale: string,
  blockers: CheckoutSubmitBlocker[] | undefined,
  /** O motivo no produto (a corrida tem o seu, `CheckoutMotiveCopy`). */
  motive: CheckoutMotiveCopy = copy.motive.trip,
): string | undefined {
  if (!blockers?.length) return undefined;
  const items = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    blockers.map((blocker) =>
      blocker === 'motive' ? motive.blocker : copy.blockers.fields[blocker],
    ),
  );
  return copy.blockers.list(items);
}

/** As linhas de preço, o CTA valorado e a nota de aprovação do cartão "Sua viagem"
 *  (`TripSummaryPanel`), no `CheckoutPriceSummary` do intelligence-ui-kit. */
export function CheckoutPricePanel({
  days,
  items,
  perTraveler,
  total,
  noun,
  submitNotice,
  onSubmit,
  onBack,
  submitting,
  canSubmit,
  submitBlockers,
  motive,
}: Readonly<CheckoutPricePanelProps>) {
  const { copy, locale } = useCheckoutEnv();
  const { price } = copy;
  const money = (value: CheckoutMoney) => formatCurrency(value.amount, value.currency, locale);
  const lines = [
    ...(items ?? []).map((line) => ({ id: line.id, label: line.label, value: money(line.price) })),
    ...(days === undefined
      ? []
      : [
          {
            id: 'dailies',
            label: price.dailies(days),
            // "3 diárias · R$ 380,75" lia como o valor DAS três; o "cada" diz que é
            // a diária média (total/noites), com o total na linha de baixo (#3217 C7).
            ...(days > 0
              ? {
                  value: price.eachDaily(
                    formatCurrency(total.amount / days, total.currency, locale),
                  ),
                }
              : {}),
          },
        ]),
    ...(perTraveler
      ? [
          {
            id: 'per-traveler',
            label: price.perTraveler,
            value: money(perTraveler),
          },
        ]
      : []),
  ];
  const blockers = blockersMessage(copy, locale, submitBlockers, motive);
  return (
    <CheckoutPriceSummary
      lines={lines}
      total={{
        label: perTraveler ? price.tripTotal : price.total,
        value: money(total),
      }}
      submitLabel={price.submitWithTotal(noun, money(total))}
      submitting={submitting}
      canSubmit={canSubmit}
      {...(blockers ? { blockers } : {})}
      {...(submitNotice ? { notice: submitNotice.text } : {})}
      {...(submitNotice?.retry === false ? { noticeRetry: false } : {})}
      onSubmit={onSubmit}
      {...(onBack ? { onBack } : {})}
      copy={{
        submitting: price.submitting,
        back: price.back,
        retry: price.retry,
        approvalNote: price.approvalNote,
      }}
    />
  );
}
